/**
 * Canvas drawing helpers for video editor text annotations (multi-line, align, justify).
 */

export type VideoTextAlign = 'left' | 'center' | 'right' | 'justify';

export interface TextAnnDraw {
  text: string;
  fontSize: number;
  fontFamily?: string;
  color: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  textAlign?: VideoTextAlign;
  backgroundColor?: string;
  /** 0..1 — max box width vs canvas width */
  textBoxWidthRatio?: number;
  x: number;
  y: number;
}

export function buildCanvasFontString(
  a: Pick<TextAnnDraw, 'fontSize' | 'fontFamily' | 'bold' | 'italic'>,
): string {
  const weight = a.bold ? 'bold ' : '';
  const style = a.italic ? 'italic ' : '';
  const size = a.fontSize;
  const fam = a.fontFamily || 'Serif';
  const quoted = fam.includes(' ') ? `"${fam}"` : fam;
  return `${style}${weight}${size}px ${quoted}, sans-serif`;
}

function wrapParagraphToLines(
  ctx: CanvasRenderingContext2D,
  paragraph: string,
  maxWidth: number,
): string[] {
  const trimmed = paragraph.trim();
  if (!trimmed) return [''];
  const words = trimmed.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export function getTextAnnotationLines(
  ctx: CanvasRenderingContext2D,
  ann: TextAnnDraw,
  canvasW: number,
): string[] {
  const maxW = Math.max(40, (ann.textBoxWidthRatio ?? 0.45) * canvasW);
  ctx.font = buildCanvasFontString(ann);
  const paras = ann.text.split('\n');
  const lines: string[] = [];
  for (const p of paras) {
    lines.push(...wrapParagraphToLines(ctx, p, maxW));
  }
  return lines.length ? lines : [''];
}

export function getTextAnnotationBounds(
  ann: TextAnnDraw,
  cw: number,
  ch: number,
  ctx: CanvasRenderingContext2D,
): { x0: number; y0: number; x1: number; y1: number } {
  ctx.font = buildCanvasFontString(ann);
  const maxW = Math.max(40, (ann.textBoxWidthRatio ?? 0.45) * cw);
  const lines = getTextAnnotationLines(ctx, ann, cw);
  const lh = ann.fontSize * 1.25;
  const ax = ann.x * cw;
  const ay = ann.y * ch;
  let maxLineW = 0;
  for (const line of lines) maxLineW = Math.max(maxLineW, ctx.measureText(line).width);
  const boxW = Math.min(maxW, Math.max(maxLineW, 8)) + 14;
  const boxH = lines.length * lh + 12;
  return {
    x0: ax - 6,
    y0: ay - ann.fontSize - 4,
    x1: ax + boxW,
    y1: ay + (lines.length - 1) * lh + 8,
  };
}

function drawUnderline(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, color: string) {
  const w = ctx.measureText(text).width;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, 1.2);
  ctx.beginPath();
  ctx.moveTo(x, y + 2);
  ctx.lineTo(x + w, y + 2);
  ctx.stroke();
  ctx.restore();
}

function drawJustifiedLine(
  ctx: CanvasRenderingContext2D,
  line: string,
  ax: number,
  maxW: number,
  y: number,
) {
  const words = line.trim().split(/\s+/);
  if (words.length < 2) {
    ctx.fillText(line, ax, y);
    return;
  }
  const widths = words.map(w => ctx.measureText(w).width);
  const spaceW = ctx.measureText(' ').width;
  const totalText = widths.reduce((a, b) => a + b, 0);
  const totalSpaces = (words.length - 1) * spaceW;
  const extra = maxW - totalText - totalSpaces;
  const gap = words.length > 1 ? extra / (words.length - 1) : 0;
  let x = ax;
  for (let i = 0; i < words.length; i++) {
    ctx.fillText(words[i], x, y);
    x += widths[i] + (i < words.length - 1 ? spaceW + gap : 0);
  }
}

export function drawTextAnnotation(
  ctx: CanvasRenderingContext2D,
  ann: TextAnnDraw,
  canvasW: number,
  canvasH: number,
  ghost: boolean,
): void {
  const maxW = Math.max(40, (ann.textBoxWidthRatio ?? 0.45) * canvasW);
  ctx.font = buildCanvasFontString(ann);
  const lines = getTextAnnotationLines(ctx, ann, canvasW);
  const lh = ann.fontSize * 1.25;
  const ax = ann.x * canvasW;
  const ay = ann.y * canvasH;
  const align = ann.textAlign || 'left';

  const bg = ann.backgroundColor;
  if (bg && bg !== 'transparent') {
    ctx.save();
    if (ghost) ctx.globalAlpha = 0.42;
    ctx.fillStyle = bg;
    const bgW = maxW + 10;
    const bgH = lines.length * lh + 10;
    ctx.fillRect(ax - 4, ay - ann.fontSize - 3, bgW, bgH);
    ctx.restore();
  }

  ctx.save();
  if (ghost) ctx.globalAlpha = 0.42;
  ctx.fillStyle = ann.color;
  ctx.shadowColor = 'rgba(0,0,0,0.75)';
  ctx.shadowBlur = ghost ? 3 : 5;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let x = ax;
    const w = ctx.measureText(line).width;
    if (align === 'center') x = ax + (maxW - w) / 2;
    else if (align === 'right') x = ax + maxW - w;
    const y = ay + i * lh;
    const lastLine = i === lines.length - 1;
    const useJustify = align === 'justify' && !lastLine && line.includes(' ');

    if (useJustify) {
      drawJustifiedLine(ctx, line, ax, maxW, y);
    } else {
      ctx.fillText(line, x, y);
    }

    if (ann.underline) {
      if (useJustify) {
        ctx.save();
        ctx.strokeStyle = ann.color;
        ctx.shadowBlur = 0;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ax, y + 2);
        ctx.lineTo(ax + maxW, y + 2);
        ctx.stroke();
        ctx.restore();
      } else {
        drawUnderline(ctx, line, x, y, ann.color);
      }
    }
  }
  ctx.restore();
}
